// API service for communicating with the conversational backend
import { ConversationMessage } from '../types';
import { appConfig } from '../config/appConfig';
import { runtimeConfig } from './runtimeConfig';
// import AuthService from './auth'; // Reserved for future SSO integration
import OidcAuthService from './oidcAuth';
import { getMockProfileRoles } from '../utils/mockProfiles';

// Configuration - Backend API URL
// Now uses appConfig instead of hardcoded values
export const getApiBaseUrl = () => runtimeConfig.getBackendUrl() || appConfig.api.baseUrl;


// Updated: Conversation history format
export interface ChatRequest {
  messages: ConversationMessage[];
  sessionId: string;
  userId?: string;
  userContext?: Record<string, any>;
  allowedAgents?: string[];
  fastMode?: boolean;  // Fast mode uses faster models (~2-3s), Precise mode uses advanced models (~4-5s)
}

// Updated: Conversation history response
export interface ChatResponse {
  messages: ConversationMessage[];
  sessionId?: string;
  timestamp?: string;
  success?: boolean;
  [key: string]: any;
}

// Streaming event types
export interface StreamingEvent {
  type: 'status' | 'tool_call' | 'tool_output' | 'response' | 'content_final' | 'error' | 'citation_list' | 'citation_source_chunks' | 'agent_switch' | 'product_display' | 'filter_alternatives' | 'routing_metadata' | 'clarification_needed' | 'done';
  status?: string;
  message?: string;  // User-friendly status message (e.g., "🔍 Searching for: 'add wearer'...")
  agent_name?: string;
  tool_name?: string;
  tool_input?: any;
  tool_output?: any;
  tool_output_details?: string;
  content?: string;
  response?: string;
  error?: string;
  // Citation-related properties
  citations?: any[];
  citation_list?: string;
  source_chunks?: any[];
  total_chunks?: number;
  // A2A routing properties
  previous_agent?: string;
  from_agent?: string;
  to_agent?: string;
  // Product display event
  anchor_id?: string;
  products?: any[];
  heading?: string;
  facets?: any[];
  // Filter alternatives event (GENERIC - any agent can use)
  unmapped_filter?: string;
  unmapped_value?: string;
  alternatives?: Array<{
    id: string;
    description: string;
    count: number;
    filters: string;
  }>;
  // Clarification event properties
  stage?: string;  // Which agent stage needs clarification
  clarification_score?: number;
  confidence?: number;
  question?: string;  // The clarification question to ask user
  ambiguities?: Array<{
    id: string;
    description: string;
    options: string[];
    nl_prompt?: string;
    clarification_type?: string;
    field_name?: string;
  }>;
  options?: string[];  // Quick options for user to pick from
  resume_context?: Record<string, any>;
  // Completion event properties
  session_id?: string;
  trace_summary?: any;
  // Generic metadata from backend (any agent can send metadata)
  // Includes tool_details with timing for done events
  metadata?: {
    tool_details?: Array<{
      tool: string;
      arguments?: any;
      timestamp?: number;
      timing?: {
        total_time_ms: number;
        query_count: number;
        queries?: Array<{
          query: string;
          time_ms: number;
          graphql?: string;
        }>;
      };
    }>;
    [key: string]: any;
  };
  routing?: Record<string, any>;
}

// Clarification state for resuming
export interface ClarificationState {
  pending: boolean;
  stage: string;
  question: string;
  options: string[];
  ambiguities: StreamingEvent['ambiguities'];
  sessionId: string;
}

export interface BackendAgent {
  id: string;
  name: string;
  endpoint: string;
  description?: string;
  category?: string;
  disabled?: boolean;
  hidden?: boolean;
  isStreaming?: boolean;
  version?: string;
  sampleQuestions?: string[];
  isNew?: boolean;
  supportsFastMode?: boolean;
  // Fields from agents.json (definitive source)
  featured?: boolean;
  routingTips?: string;
  introMessage?: string;
  shortIntroMessage?: string;
  // Project / feedback contact email surfaced on the welcome card outside the
  // collapsed welcome message (so it stays visible for returning users).
  projectContactEmail?: string;
  // Release lifecycle fields
  releaseDate?: string;
  demoDate?: string;
  uatStartDate?: string;
  uatEndDate?: string;
  lastUpdated?: string;
  versionHistory?: { version: string; date: string; notes: string }[];
  // Home-screen update notice / "What's New"
  updateNotice?: { enabled: boolean; date?: string; summary?: string };
  // Agent contacts
  contacts?: { name: string; role: string; email: string; context?: string }[];
  // Work items
  workItems?: { title: string; date?: string; status?: 'pending' | 'in_progress' | 'done'; notes?: string }[];
  // Access channels (alternative surfaces like embedded widgets)
  accessChannels?: { id: string; type: string; label: string; description: string; url: string; placement?: string; enabled: boolean }[];
}

export interface UserProfile {
  email: string;
  name: string;
  roles: string[];
  accessibleAgents: string[];
}

export interface AgentsResponse {
  agents: BackendAgent[];
  user?: UserProfile;
  gateway: string;
  apimGateway?: string;
}

export interface HealthResponse {
  status: string;
  timestamp: string;
  uptime: number;
  gateway: string;
  apimEnabled: boolean;
  localRateLimitEnabled: boolean;
}

class ApiService {
  private baseUrl: string;
  private activeStreamControllers: Set<AbortController> = new Set();
  private cachedToken: string | null = null;
  private cachedTokenType: 'access_token' | 'id_token' | null = null;
  private tokenExpiry: number = 0;
  private tokenFetchPromise: Promise<string | null> | null = null;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl || getApiBaseUrl();
  }

  // Get mock profile headers for local dev role simulation
  private getMockProfileHeaders(): Record<string, string> {
    const mockRoles = getMockProfileRoles();
    if (!mockRoles) return {};
    return {
      'x-mock-profile-roles': JSON.stringify(mockRoles),
      'x-mock-profile-email': `mock-${mockRoles.find(r => r !== 'authenticated') || 'user'}@test.unifirst.com`,
    };
  }

  // Get authentication headers for API requests (with caching to avoid repeated /.auth/me calls)
  private async getAuthHeaders(): Promise<Record<string, string>> {
    // When a mock profile is active on localhost, send role headers
    // instead of a real auth token so the backend RBAC runs with those roles
    const mockHeaders = this.getMockProfileHeaders();
    if (Object.keys(mockHeaders).length > 0) {
      return mockHeaders;
    }

    // Return cached token if still valid
    if (this.cachedToken && Date.now() < this.tokenExpiry) {
      return { 'Authorization': `Bearer ${this.cachedToken}` };
    }
    
    // If already fetching, wait for that promise
    if (this.tokenFetchPromise) {
      const token = await this.tokenFetchPromise;
      if (token) {
        return { 'Authorization': `Bearer ${token}` };
      }
      return {};
    }
    
    // Fetch new token
    this.tokenFetchPromise = this.fetchAuthToken();
    const token = await this.tokenFetchPromise;
    this.tokenFetchPromise = null;
    
    if (token) {
      return { 'Authorization': `Bearer ${token}` };
    }
    return {};
  }
  
  // Fetch and cache authentication token
  private async fetchAuthToken(): Promise<string | null> {
    // Always use OIDC service (Easy Auth is disabled in our environment)
    const oidcService = OidcAuthService.getInstance();

    try {
      await oidcService.ensureInitialized();
    } catch (error) {
      console.error('❌ Failed to initialize OIDC service:', error);
      return null;
    }

    try {
      // Prefer ID token (contains app roles)
      let token = await oidcService.getIdToken();
      let tokenType: 'access_token' | 'id_token' = 'id_token';

      // Fall back to access token if ID token missing
      if (!token) {
        token = await oidcService.getAccessToken();
        tokenType = 'access_token';
      }

      console.log('🔍 OIDC token result:', {
        tokenType,
        hasToken: !!token,
        tokenLength: token?.length,
        tokenPreview: token ? `${token.substring(0, 20)}...` : 'null'
      });

      if (token) {
        console.log(`✅ Cached OIDC ${tokenType} for 50 minutes`);
        this.cachedToken = token;
        this.cachedTokenType = tokenType;
        this.tokenExpiry = Date.now() + (50 * 60 * 1000); // Cache for 50 minutes
        return token;
      }

      console.warn('⚠️ OIDC service returned neither ID token nor access token');
    } catch (error) {
      console.error('❌ Failed to obtain OIDC token:', error);
    }
    
    console.error('🚨 NO ACCESS TOKEN AVAILABLE - API calls WILL fail with 401/403');
    return null;
  }

  // Generic request handler with error handling
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    // Get authentication headers
    const authHeaders = await this.getAuthHeaders();
    
    const defaultOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
        ...options.headers,
      },
      credentials: 'include', // Include authentication cookies/tokens
    };

    const requestOptions = { ...defaultOptions, ...options };

    try {
      const response = await fetch(url, requestOptions);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ 
          error: 'Network error', 
          message: `HTTP ${response.status}: ${response.statusText}` 
        }));
        
        throw new ApiError(
          response.status,
          errorData.error || 'Request failed',
          errorData.message || response.statusText,
          errorData
        );
      }

      return await response.json();
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      
      // Network or other errors
      throw new ApiError(
        0,
        'Network Error',
        error instanceof Error ? error.message : 'Unable to connect to the server',
        { originalError: error }
      );
    }
  }

  // Health check
  async healthCheck(): Promise<HealthResponse> {
    return this.request<HealthResponse>('/health');
  }

  // Get available agents (including hidden ones for routing)
  async getAgents(): Promise<AgentsResponse> {
    return this.request<AgentsResponse>('/agents?includeHidden=true');
  }

  // Get all agents (including disabled) for admin use
  async getAllAgents(): Promise<AgentsResponse> {
    return this.request<AgentsResponse>('/agents?includeDisabled=true&includeHidden=true');
  }

  // Get agents discovered from Azure resource group
  async discoverAgents(): Promise<AgentsResponse> {
    return this.request<AgentsResponse>('/agents/discover');
  }

  // Update agent status (enable/disable)
  async updateAgentStatus(
    agentId: string, 
    updates: { disabled?: boolean }
  ): Promise<{ success: boolean; agent: BackendAgent; message: string }> {
    return this.request<{ success: boolean; agent: BackendAgent; message: string }>(
      `/agents/${agentId}/status`,
      {
        method: 'PUT',
        body: JSON.stringify(updates),
      }
    );
  }

  // Updated: Send conversation history to specific agent
  async sendConversationMessage(
    agentId: string,
    messages: ConversationMessage[],
    sessionId: string,
    userId?: string,
    isRegeneration?: boolean
  ): Promise<ChatResponse> {
    // Special handling for webapp agent - it always returns SSE, so convert to streaming call
    if (this.isRealStreamingAgent(agentId)) {
      console.log('⚠️ WebApp agent called through non-streaming method - converting to streaming');
      
      return new Promise((resolve, reject) => {
        this.sendStreamingConversationMessage(
          agentId,
          messages,
          sessionId,
          (event) => {
            // Event handler - no need to manually accumulate
          },
          (finalResponse) => {
            // Convert streaming response to regular ChatResponse format
            resolve({
              messages: [
                { role: 'assistant', content: finalResponse }
              ],
              sessionId,
              timestamp: new Date().toISOString(),
              success: true
            });
          },
          (error) => {
            reject(error);
          },
          userId
        );
      });
    }

    const requestBody: ChatRequest = {
      messages,
      sessionId,
      ...(userId && { userId })
    };

    // Only add custom headers if needed
    const requestOptions: any = {
      method: 'POST',
      body: JSON.stringify(requestBody),
    };

    if (isRegeneration) {
      requestOptions.headers = { 
        'Content-Type': 'application/json',
        'x-regeneration': 'true' 
      };
      console.log('🔄 Regeneration request - Body:', requestBody);
      console.log('🔄 Regeneration request - Options:', requestOptions);
    }

    return this.request<ChatResponse>(`/${agentId}/chat`, requestOptions);
  }

  // Legacy method for backward compatibility
  async sendChatMessage(
    agentId: string,
    message: string,
    sessionId: string,
    userId?: string
  ): Promise<ChatResponse> {
    // Convert single message to conversation format
    const messages: ConversationMessage[] = [
      { role: 'user', content: message }
    ];
    
    return this.sendConversationMessage(agentId, messages, sessionId, userId);
  }

  // Check specific agent health (basic connectivity)
  async checkAgentHealth(agentId: string): Promise<any> {
    return this.request(`/${agentId}/health`);
  }

  // Check functional agent health (includes conversational capability)
  async checkAgentFunctionalHealth(agentId: string): Promise<any> {
    return this.request(`/agents/${agentId}/health/functional`);
  }

  // Get backend configuration (for debugging)
  async getConfig(): Promise<any> {
    return this.request('/config');
  }

  // Parse Server-Sent Events (SSE) data
  private parseSSEData(data: string): StreamingEvent | null {
    // LOG RAW DATA to see what's actually coming from the server
    console.log('📥 RAW SSE DATA:', data.substring(0, 200));
    
    try {
      // Remove 'data: ' prefix if present
      const cleanData = data.replace(/^data:\s*/, '').trim();
      
      if (!cleanData || cleanData === '[DONE]') {
        console.log('📥 SSE: Empty or [DONE]');
        return null;
      }

      const parsed = JSON.parse(cleanData);
      console.log('📥 SSE PARSED TYPE:', parsed.type);
      
      // Handle different response formats
      if (parsed.type === 'content' && parsed.content !== undefined) {
        // WebApp agent format: {"type": "content", "content": "text"}
        return {
          type: 'response',
          content: parsed.content
        };
      } else if (parsed.type === 'content_final' && parsed.content !== undefined) {
        // Final content with anchors inserted - replaces streamed content
        // This ensures [PRODUCT_CARDS: anchor_id] markers are present for product display rendering
        return {
          type: 'content_final',
          content: parsed.content
        };
      } else if (parsed.type === 'status') {
        // WebApp agent status format: {"type": "status", "status": "processing"}
        // Also handles tool calling: {"type": "status", "status": "tool_calling", "tool_name": "query_search_index", "message": "🔍 Searching..."}
        return {
          type: 'status',
          status: parsed.status || parsed.agent_name || 'processing',
          message: parsed.message, // User-friendly status message
          tool_name: parsed.tool_name,
          tool_output: parsed.tool_output,
          tool_output_details: parsed.tool_output_details
        };
      } else if (parsed.type === 'agent_switch') {
        // A2A agent switching event: {"type": "agent_switch", "agent_name": "new_agent", "previous_agent": "old_agent"}
        return {
          type: 'agent_switch',
          agent_name: parsed.agent_name,
          previous_agent: parsed.previous_agent,
          from_agent: parsed.from_agent,
          to_agent: parsed.to_agent
        };
      } else if (parsed.type === 'citation_list' && parsed.citation_list !== undefined) {
        // Citation list event: {"type": "citation_list", "citation_list": "Sources:\n1. Document.pdf...", "citations": [...]}
        console.log('📚 CITATION LIST EVENT RECEIVED:', {
          citation_list: parsed.citation_list?.substring(0, 100),
          citationsCount: parsed.citations?.length || 0,
          firstCitation: parsed.citations?.[0] ? {
            id: parsed.citations[0].citation_id,
            filename: parsed.citations[0].filename
          } : 'none'
        });
        return {
          type: 'citation_list',
          citation_list: parsed.citation_list,
          citations: parsed.citations
        };
      } else if (parsed.type === 'citation_source_chunks' && parsed.source_chunks !== undefined) {
        // Citation source chunks event: {"type": "citation_source_chunks", "source_chunks": [...]}
        console.log('📄 CITATION SOURCE CHUNKS EVENT RECEIVED:', {
          chunksCount: parsed.source_chunks?.length || 0,
          firstChunk: parsed.source_chunks?.[0] ? {
            id: parsed.source_chunks[0].citation_id,
            filename: parsed.source_chunks[0].filename,
            contentPreview: parsed.source_chunks[0].content?.substring(0, 50)
          } : 'none'
        });
        return {
          type: 'citation_source_chunks',
          source_chunks: parsed.source_chunks,
          total_chunks: parsed.total_chunks
        };
      } else if (parsed.type === 'product_display' && Array.isArray(parsed.products)) {
        console.log('🎁 PRODUCT_DISPLAY EVENT PARSED IN API.TS:', {
          anchor_id: parsed.anchor_id,
          productCount: parsed.products?.length || 0,
          facetCount: parsed.facets?.length || 0,
          facetNames: parsed.facets?.map((f: any) => f.field_name) || [],
          heading: parsed.heading || 'N/A',
          firstProductName: parsed.products?.[0]?.name?.substring(0, 50) || 'N/A'
        });
        return {
          type: 'product_display',
          anchor_id: parsed.anchor_id,
          products: parsed.products,
          heading: parsed.heading || '',
          facets: parsed.facets || []
        } as StreamingEvent;
      } else if (parsed.type === 'tool_call') {
        // Tool call event with name and arguments
        // Arguments may come as a JSON string that needs parsing
        let toolInput = parsed.tool_input || {};
        if (parsed.arguments) {
          try {
            // Try to parse if it's a JSON string
            toolInput = typeof parsed.arguments === 'string' 
              ? JSON.parse(parsed.arguments) 
              : parsed.arguments;
          } catch {
            // If parsing fails, use as-is (might be a plain string)
            toolInput = parsed.arguments;
          }
        }
        console.log('🔧 TOOL_CALL EVENT:', {
          tool_name: parsed.tool_name,
          arguments: typeof toolInput === 'string' ? toolInput.substring(0, 100) : toolInput
        });
        return {
          type: 'tool_call',
          tool_name: parsed.tool_name,
          tool_input: toolInput
        } as StreamingEvent;
      } else if (parsed.type === 'tool_output') {
        // Tool output event with result (includes timing data)
        console.log('🔧 TOOL_OUTPUT EVENT:', {
          tool_name: parsed.tool_name,
          output_length: parsed.output?.length || 0,
          hasOutput: !!parsed.output
        });
        return {
          type: 'tool_output',
          tool_name: parsed.tool_name,
          tool_output: parsed.output || parsed.tool_output
        };
      }
      
      // Log ALL event types to see what's coming through
      console.log('📨 SSE EVENT RECEIVED:', {
        type: parsed.type,
        hasMetadata: !!parsed.metadata,
        keys: Object.keys(parsed)
      });
      
      // Return as-is if already in expected format
      return parsed;
    } catch (error) {
      console.warn('Failed to parse SSE data:', data, error);
      return null;
    }
  }

  // Streaming conversation message with memory leak prevention
  async sendStreamingConversationMessage(
    agentId: string,
    messages: ConversationMessage[],
    sessionId: string,
    onEvent: (event: StreamingEvent) => void,
    onComplete: (fullResponse: string) => void,
    onError: (error: Error) => void,
    userId?: string,
    _retryViaHttpsGateway?: boolean,
    messageContext?: any
  ): Promise<void> {
    console.log('🌐 API SERVICE STREAMING REQUEST', {
      agentId,
      messagesCount: messages.length,
      sessionId,
      userId,
      baseUrl: this.baseUrl,
      lastMessage: messages[messages.length - 1]?.content?.slice(0, 100)
    });
    const tSendStart = Date.now();
    let firstHeaderAt: number | null = null;
    let firstChunkAt: number | null = null;
    const eventCounters: Record<string, number> = {
      response: 0,
      status: 0,
      agent_switch: 0,
      product_display: 0,
      error: 0,
      citation_list: 0,
      citation_source_chunks: 0
    };
    
    // Enhanced prompting for A2A: When using Aldo, enhance HR questions to request verbatim forwarding
    let effectiveAgentId = agentId;
    const isHRQuestion = messages.some(msg => 
      msg.role === 'user' && (
        msg.content.toLowerCase().includes('harassment') ||
        msg.content.toLowerCase().includes('hr policy') ||
        msg.content.toLowerCase().includes('workplace') ||
        msg.content.toLowerCase().includes('report') ||
        msg.content.toLowerCase().includes('discrimination') ||
        msg.content.toLowerCase().includes('complaint')
      )
    );
    
    if (agentId === 'aldo' && isHRQuestion) {
      console.log(`🧪 TESTING: Attempting to get true A2A response from Aldo with enhanced prompting`);
      
      // Enhance the last user message to request verbatim forwarding
      const lastUserMessage = messages[messages.length - 1];
      if (lastUserMessage && lastUserMessage.role === 'user') {
        lastUserMessage.content += `\n\n[SYSTEM INSTRUCTION: Please route this to the HR Policy agent and forward their complete response with all citations, policy numbers, and specific details verbatim. Do not summarize or interpret - provide the full detailed response exactly as received from the HR Policy specialist.]`;
        console.log(`🧪 Enhanced prompt for A2A forwarding`);
      }
    }
    
    // Sanitize and shrink payload to avoid huge request bodies blocking server header flush
    const sanitizeConversation = (msgs: ConversationMessage[]): ConversationMessage[] => {
      return msgs.map(m => {
        return {
          ...m,
          content: m.content || ''
        } as ConversationMessage;
      });
    };

    const sanitizedMessages = sanitizeConversation(messages);
    
    // Add CONTEXT_ENVELOPE for product agent with pinned products
    let messagesWithContext = sanitizedMessages;
    if ((agentId === 'product' || agentId.includes('product')) && messageContext?.pinnedProducts) {
      const envelope = {
        facts_ledger: {
          pinned_skus: messageContext.pinnedProducts || [],
          dropped_skus: [],
          active_tabs: (messageContext.activeProducts || []).map((p: any) => p.sku),
          filters_applied: {}
        },
        summary: {
          text: ''
        }
      };
      
      // Add context envelope as first system message
      const contextMessage: ConversationMessage = {
        role: 'system',
        content: `CONTEXT_ENVELOPE: ${JSON.stringify(envelope)}`
      };
      
      messagesWithContext = [contextMessage, ...sanitizedMessages];
      console.log('🎯 Added CONTEXT_ENVELOPE for product agent:', {
        pinnedCount: envelope.facts_ledger.pinned_skus.length,
        activeCount: envelope.facts_ledger.active_tabs.length
      });
    }

    // All agents use /chat/stream by default — no hardcoded agent list needed.
    // Only Aldo has a special hand-off endpoint.
    let url: string;
    let fallbackUrl: string | undefined;
    if (effectiveAgentId === 'aldo') {
      url = `${this.baseUrl}/${effectiveAgentId}/chat/stream/hand-off`;
      fallbackUrl = undefined;
    } else {
      // Default: every agent uses /chat/stream (configured in agents.json)
      url = `${this.baseUrl}/${effectiveAgentId}/chat/stream`;
    }
    
    // Build userContext by merging user profile data with routing info
    const userContext = messageContext ? {
      // Spread the user profile fields (displayName, email, city, state, etc.)
      ...(messageContext.userContext || {}),
      // Include routing context for follow-up handling
      ...(messageContext.routing && { routing: messageContext.routing })
    } : undefined;
    
    const requestBody: ChatRequest = {
      messages: messagesWithContext,
      sessionId,
      ...(userId && { userId }),
      ...(userContext && { userContext }),
      ...(messageContext?.routing?.accessibleAgents && {
        allowedAgents: messageContext.routing.accessibleAgents
      }),
      // Fast mode toggle - defaults to false (Precise mode) if not specified
      fastMode: messageContext?.fastMode ?? false
    };

    try {
      if (
        typeof window !== 'undefined' &&
        (effectiveAgentId === 'product' || effectiveAgentId.includes('product')) &&
        window.localStorage.getItem('ab_product_responses') === 'true'
      ) {
        (requestBody as any).orchestrator = 'responses';
        console.log('🧪 A/B TEST: Using Responses orchestrator for product agent');
      }
    } catch (err) {
      console.warn('Unable to read A/B flag for product Responses orchestrator', err);
    }

    // Log payload sizes to diagnose header stalls
    // try {
    //   const rawBytes = new TextEncoder().encode(JSON.stringify({ messages, sessionId, userId })).length;
    //   const sanitizedBytes = new TextEncoder().encode(JSON.stringify(requestBody)).length;
    //   console.log('📤 Payload sizes (bytes)', { rawBytes, sanitizedBytes, reducedBy: rawBytes - sanitizedBytes });
    // } catch {}

    // console.log(`📤 Sending request to ${effectiveAgentId}:`, JSON.stringify(requestBody, null, 2));

    // Memory leak prevention configuration
    const MAX_RESPONSE_SIZE = 1024 * 1024; // 1MB limit
    const MAX_BUFFER_SIZE = 64 * 1024; // 64KB buffer limit
    const TIMEOUT_MS = 120 * 1000; // 120s timeout for complex agent operations (product search, A2A routing)
    const HEADERS_TIMEOUT_MS = 30 * 1000; // 30s for initial headers (increased for A2A routing)
    const MEMORY_WARNING_THRESHOLD = 512 * 1024; // 512KB warning threshold
    
    let timeoutId: NodeJS.Timeout | undefined;
    let headersTimeoutId: NodeJS.Timeout | undefined;
    let abortedByHeadersTimeout = false;
    let abortController: AbortController | undefined;
    
    // Create abort controller for timeout handling
    abortController = new AbortController();
    // Track controller for global cancellation on conversation switches
    this.activeStreamControllers.add(abortController);
    
    // Set up timeout handling
    timeoutId = setTimeout(() => {
      if (abortController) {
        abortController.abort();
      }
    }, TIMEOUT_MS);

    const tryFetch = async (fetchUrl: string, isRetry: boolean = false): Promise<Response> => {
      // Get authentication headers
      const authHeaders = await this.getAuthHeaders();
      
      console.log('📡 MAKING FETCH REQUEST', {
        url: fetchUrl,
        method: 'POST',
        isRetry,
        bodySize: JSON.stringify(requestBody).length,
        agentId: effectiveAgentId,
        hasAuthHeader: !!authHeaders['Authorization'],
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        }
      });
      
      const response = await fetch(fetchUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          ...authHeaders, // Include authentication headers
        },
        body: JSON.stringify(requestBody),
        credentials: 'include', // Include authentication cookies/tokens
        signal: abortController?.signal
      });
      
      console.log('📡 FETCH RESPONSE RECEIVED', {
        url: fetchUrl,
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        contentType: response.headers.get('content-type'),
        hasBody: !!response.body
      });
      if (!firstHeaderAt) {
        firstHeaderAt = Date.now();
        console.log('⏱️ TTFB_HEADERS_MS', firstHeaderAt - tSendStart);
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ 
          error: 'Network error', 
          message: `HTTP ${response.status}: ${response.statusText}` 
        }));
        
        // If hand-off endpoint fails with 404, 503, or 502 and we have a fallback, try the fallback
        if ((response.status === 404 || response.status === 503 || response.status === 502) && fallbackUrl && !isRetry) {
          console.log(`🔄 Hand-off endpoint failed (${response.status}), trying fallback: ${fallbackUrl}`);
          return await tryFetch(fallbackUrl, true);
        }
        
        throw new ApiError(
          response.status,
          errorData.error || 'Request failed',
          errorData.message || response.statusText,
          errorData
        );
      }

      return response;
    };

    try {
      try {
        const bodyBytes = new TextEncoder().encode(JSON.stringify(requestBody)).length;
        console.log('📤 STREAM FETCH START', {
          url,
          agentId: effectiveAgentId,
          messagesCount: sanitizedMessages.length,
          bodyBytes,
          sessionId,
          userId,
          timeSinceSendStartMs: Date.now() - tSendStart
        });
      } catch {}

      // Start a watchdog for missing headers
      headersTimeoutId = setTimeout(() => {
        if (!firstHeaderAt && abortController) {
          abortedByHeadersTimeout = true;
          console.error('⛔ NO_HEADERS_TIMEOUT_MS', { timeoutMs: HEADERS_TIMEOUT_MS, url });
          abortController.abort();
        }
      }, HEADERS_TIMEOUT_MS);

      const response = await tryFetch(url);

      console.log('📡 STREAM FETCH RESPONSE OBTAINED', {
        status: response.status,
        ok: response.ok,
        redirected: response.redirected,
        url,
        timeSinceSendStartMs: Date.now() - tSendStart
      });

      if (headersTimeoutId) {
        clearTimeout(headersTimeoutId);
        headersTimeoutId = undefined;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body available for streaming');
      }

      let buffer = '';
      let fullResponse = '';
      const decoder = new TextDecoder();
      let totalBytesProcessed = 0;

      try {
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            break;
          }

          const chunk = decoder.decode(value, { stream: true });
          totalBytesProcessed += chunk.length;
          if (!firstChunkAt) {
            firstChunkAt = Date.now();
            console.log('⏱️ TTFB_FIRST_CHUNK_MS', firstChunkAt - tSendStart);
          }
          
          // Check response size limits
          if (fullResponse.length + chunk.length > MAX_RESPONSE_SIZE) {
            throw new ApiError(
              413,
              'Response Too Large',
              `Response size exceeds ${MAX_RESPONSE_SIZE} bytes limit`,
              { currentSize: fullResponse.length + chunk.length, limit: MAX_RESPONSE_SIZE }
            );
          }
          
          buffer += chunk;
          
          // Check buffer size limits
          if (buffer.length > MAX_BUFFER_SIZE) {
            console.warn(`Buffer size (${buffer.length} bytes) exceeds recommended limit (${MAX_BUFFER_SIZE} bytes)`);
            
            // Force process buffer to prevent memory issues
            const lines = buffer.split('\n');
            buffer = lines.pop() || ''; // Keep only the last incomplete line
            
            // Process all complete lines
            for (const line of lines) {
              if (line.trim()) {
                const event = this.parseSSEData(line);
                if (event) {
                  onEvent(event);
                  
                  // Collect response content — content_final replaces the streamed body
                  if (event.type === 'response' && event.content !== undefined) {
                    fullResponse += event.content;
                  } else if (event.type === 'content_final' && event.content !== undefined) {
                    fullResponse = event.content;
                  }
                  if (typeof eventCounters[event.type] === 'number') {
                    eventCounters[event.type] += 1;
                  }
                }
              }
            }
          } else {
            // Normal processing
            const lines = buffer.split('\n');
            buffer = lines.pop() || ''; // Keep incomplete line in buffer

            for (const line of lines) {
              if (line.trim()) {
                const event = this.parseSSEData(line);
                if (event) {
                  onEvent(event);

                  // Collect response content — content_final replaces the streamed body
                  if (event.type === 'response' && event.content !== undefined) {
                    fullResponse += event.content;
                  } else if (event.type === 'content_final' && event.content !== undefined) {
                    fullResponse = event.content;
                  }
                  if (typeof eventCounters[event.type] === 'number') {
                    eventCounters[event.type] += 1;
                  }
                }
              }
            }
          }
          
          // Memory usage warning
          if (fullResponse.length > MEMORY_WARNING_THRESHOLD) {
            console.warn(`Streaming response size (${fullResponse.length} bytes) exceeds warning threshold (${MEMORY_WARNING_THRESHOLD} bytes)`);
          }
        }

        // Process any remaining buffer
        if (buffer.trim()) {
          const event = this.parseSSEData(buffer);
          if (event) {
            onEvent(event);
            if (event.type === 'response' && event.content !== undefined) {
              fullResponse += event.content;
            }
            if (typeof eventCounters[event.type] === 'number') {
              eventCounters[event.type] += 1;
            }
          }
        }
        
        // Clear timeout since we completed successfully
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        if (headersTimeoutId) {
          clearTimeout(headersTimeoutId);
          headersTimeoutId = undefined;
        }
        
        const totalMs = Date.now() - tSendStart;
        console.log(`Streaming completed: ${totalBytesProcessed} bytes processed, ${fullResponse.length} bytes in response`);
        console.log('⏱️ STREAM_TIMINGS', {
          totalMs,
          ttfbHeadersMs: firstHeaderAt ? firstHeaderAt - tSendStart : null,
          ttfbFirstChunkMs: firstChunkAt ? firstChunkAt - tSendStart : null,
          counters: eventCounters
        });
        
        if (fullResponse.length === 0) {
          console.warn('⚠️ Empty response from agent - this may indicate an API issue');
          console.warn('🔍 Debug info:', { agent: effectiveAgentId, bytesProcessed: totalBytesProcessed });
        }
        
        onComplete(fullResponse);
        
      } finally {
        // Always release the reader
        reader.releaseLock();
        
        // Cleanup resources
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        if (abortController) {
          this.activeStreamControllers.delete(abortController);
        }
      }

    } catch (error) {
      // Cleanup on error
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (headersTimeoutId) {
        clearTimeout(headersTimeoutId);
        headersTimeoutId = undefined;
      }
      if (abortController) {
        this.activeStreamControllers.delete(abortController);
      }
      
      if (error instanceof ApiError) {
        console.error('⛔ STREAM ERROR (ApiError)', {
          status: error.status,
          error: error.error,
          message: error.message,
          details: error.details
        });
        onError(error);
      } else if (error instanceof Error && error.name === 'AbortError') {
        if (abortedByHeadersTimeout) {
          console.error('⛔ STREAM ERROR (AbortError - No headers)', { headersTimeoutMs: HEADERS_TIMEOUT_MS, baseUrl: this.baseUrl });
          onError(new ApiError(
            408,
            'Request Timeout',
            `No response headers received within ${HEADERS_TIMEOUT_MS / 1000} seconds`,
            { headersTimeout: HEADERS_TIMEOUT_MS }
          ));
        } else {
          console.error('⛔ STREAM ERROR (AbortError)', { timeoutMs: TIMEOUT_MS });
          onError(new ApiError(
            408,
            'Request Timeout',
            `Streaming request timed out after ${TIMEOUT_MS / 1000} seconds`,
            { timeout: TIMEOUT_MS }
          ));
        }
      } else {
        console.error('⛔ STREAM ERROR (Unknown)', { message: (error as any)?.message });
        onError(new ApiError(
          0,
          'Streaming Error',
          error instanceof Error ? error.message : 'Streaming request failed',
          { originalError: error }
        ));
      }
    }
  }

  // Cancel all active streaming requests (used when switching conversations)
  public cancelAllStreams(): void {
    try {
      this.activeStreamControllers.forEach((controller) => {
        try { controller.abort(); } catch {}
      });
    } finally {
      this.activeStreamControllers.clear();
    }
  }

  // Simulate streaming for non-streaming agents
  async simulateStreamingResponse(
    response: string,
    onChunk: (chunk: string) => void,
    onComplete: () => void,
    delay: number = 25
  ): Promise<void> {
    const words = response.split(' ');
    let currentText = '';
    
    for (let i = 0; i < words.length; i++) {
      const word = words[i];
      currentText += (i === 0 ? '' : ' ') + word;
      
      onChunk(currentText);
      
      // Add realistic delays - longer for punctuation
      const hasEndPunctuation = /[.!?]$/.test(word);
      const hasComma = /,$/.test(word);
      const currentDelay = hasEndPunctuation ? delay * 4 : hasComma ? delay * 2 : delay;
      
      await new Promise(resolve => setTimeout(resolve, currentDelay));
    }
    
    onComplete();
  }

  // Check if agent supports streaming based on preference
  isStreamingAgent(agentId: string, streamingPreference: boolean = false): boolean {
    // Use the streaming preference to determine if streaming should be enabled
    // For now, we use simulated streaming when streaming preference is true
    return streamingPreference;
  }

  // Check if agent supports real streaming (not simulated)
  // All agents are real streaming agents — configured in agents.json with isStreaming: true
  isRealStreamingAgent(_agentId: string): boolean {
    return true;
  }
}

// Custom error class for API errors
export class ApiError extends Error {
  constructor(
    public status: number,
    public error: string,
    message: string,
    public details?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }

  // Helper methods for common error types
  isNetworkError(): boolean {
    return this.status === 0;
  }

  isServerError(): boolean {
    return this.status >= 500;
  }

  isClientError(): boolean {
    return this.status >= 400 && this.status < 500;
  }

  isAuthError(): boolean {
    return this.status === 401 || this.status === 403;
  }

  isNotFound(): boolean {
    return this.status === 404;
  }

  isRateLimit(): boolean {
    return this.status === 429;
  }
}

// Create and export a singleton instance
export const apiService = new ApiService();

// Export the class for testing or custom instances
export { ApiService }; 